import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Search, Plus, ClipboardList, Download, CalendarDays } from 'lucide-react';
import productionService from '../../../services/productionService';
import { wsService } from '../../../services/websocket';
import { useLanguage } from '../../../i18n/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import { isElevatedRole } from '../../../constants/queryConfig';
import SessionDetailsModal, { getDefaultSessionTab } from './SessionDetailsModal';
import AddSessionModal from './AddSessionModal';
import WeeklyPlanningView from './WeeklyPlanningView';
import ExportSessionsModal from './ExportSessionsModal';
import CalendarView from './CalendarView';
import SessionTableSection from './SessionTableSection';
import CancelSessionModal from '../../../components/CancelSessionModal';

interface ManagerScheduleViewProps {
  initialDate?: string | null;
}

const ManagerScheduleView: React.FC<ManagerScheduleViewProps> = ({ initialDate }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Only managers and admins can add/edit/cancel sessions (sessions are cancelled, never deleted)
  const canManageSessions = isElevatedRole(user?.role);

  const [searchTerm, setSearchTerm] = useState('');
  // Selected date: today by default. null = show entire visible range.
  const [selectedDate, setSelectedDate] = useState<string | null>(
    initialDate || new Date().toLocaleDateString('en-CA')
  );
  // Visible date range from CalendarView (set by onRangeChange callback)
  const [visibleRange, setVisibleRange] = useState<{ start: string; end: string } | null>(null);

  // Calculate initial week offset if initialDate is provided
  // The week view shows Sun-Sat, so we need to find which week contains the target date
  const calculateInitialWeekOffset = () => {
    if (!initialDate) return 0;

    const today = new Date();
    const targetDate = new Date(initialDate);

    // Get the start of the current week (Sunday)
    const todayStartOfWeek = new Date(today);
    todayStartOfWeek.setDate(today.getDate() - today.getDay());
    todayStartOfWeek.setHours(0, 0, 0, 0);

    // Get the start of the target week (Sunday)
    const targetStartOfWeek = new Date(targetDate);
    targetStartOfWeek.setDate(targetDate.getDate() - targetDate.getDay());
    targetStartOfWeek.setHours(0, 0, 0, 0);

    // Calculate difference in weeks
    const diffTime = targetStartOfWeek.getTime() - todayStartOfWeek.getTime();
    const diffWeeks = Math.round(diffTime / (1000 * 60 * 60 * 24 * 7));

    return diffWeeks;
  };

  const [weekOffset, setWeekOffset] = useState(calculateInitialWeekOffset());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [viewMode, setViewMode] = useState<'view' | 'plan'>('view');
  // Track whether user has navigated away from default (today) view
  const [hasNavigated, setHasNavigated] = useState(false);

  // Edit state — holds full session data fetched from GET /:id for the AddSessionModal
  const [editingSession, setEditingSession] = useState<any | null>(null);
  const [loadingEditSession, setLoadingEditSession] = useState(false);

  // Cancel confirmation state
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);

  // Session details modal state
  const [selectedSession, setSelectedSession] = useState<any | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Cancelled sessions collapsed state (auto-collapsed by default)
  const [isActiveExpanded, setIsActiveExpanded] = useState(true);
  const [isPlannedExpanded, setIsPlannedExpanded] = useState(true);
  const [isCompletedExpanded, setIsCompletedExpanded] = useState(true);
  const [isCancelledExpanded, setIsCancelledExpanded] = useState(false);

  // Fetch production sessions
  const { data: sessions = [], isLoading } = useQuery<any[]>(
    ['productionSessions'],
    () => productionService.getProductionSessions(),
    {
      keepPreviousData: true,
      staleTime: 20000,
    }
  );

  // WebSocket for real-time updates
  useEffect(() => {
    wsService.connect();

    const handleSessionCreated = () => {
      queryClient.invalidateQueries(['productionSessions']);
    };

    const handleSessionUpdated = () => {
      queryClient.invalidateQueries(['productionSessions']);
    };

    wsService.on('production-session-created', handleSessionCreated);
    wsService.on('production-session-updated', handleSessionUpdated);
    wsService.on('box-packed', handleSessionUpdated);
    wsService.on('box-cancelled', handleSessionUpdated);
    wsService.on('box-deleted', handleSessionUpdated);
    wsService.on('pending-label-voided', handleSessionUpdated);

    return () => {
      wsService.off('production-session-created', handleSessionCreated);
      wsService.off('production-session-updated', handleSessionUpdated);
      wsService.off('box-packed', handleSessionUpdated);
      wsService.off('box-cancelled', handleSessionUpdated);
      wsService.off('box-deleted', handleSessionUpdated);
      wsService.off('pending-label-voided', handleSessionUpdated);
    };
  }, [queryClient]);

  // Handle opening session details - updates URL
  const openSessionDetails = useCallback((session: any) => {
    setSelectedSession(session);
    setSearchParams({ session: session.session_number });
  }, [setSearchParams]);

  // Handle closing session details - clears URL
  const closeSessionDetails = useCallback(() => {
    setSelectedSession(null);
    setSearchParams({});
  }, [setSearchParams]);

  // Restore session from URL on page load (only once when sessions load)
  const [hasRestoredFromUrl, setHasRestoredFromUrl] = useState(false);
  useEffect(() => {
    if (hasRestoredFromUrl) return;
    const sessionIdFromUrl = searchParams.get('session');
    if (sessionIdFromUrl && sessions.length > 0) {
      const session = sessions.find((s: any) => s.session_number === sessionIdFromUrl);
      if (session) {
        setSelectedSession(session);
      }
      setHasRestoredFromUrl(true);
    }
  }, [sessions, searchParams, hasRestoredFromUrl]);

  // Cancelling a session goes through the shared <CancelSessionModal> (WCO-2706) — it owns the
  // reason dropdown, the password confirmation and the mutation.

  // Calculate week dates based on offset
  const weekDates = useMemo(() => {
    const today = new Date();
    today.setDate(today.getDate() + weekOffset * 7);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    const dates: string[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date.toLocaleDateString('en-CA'));
    }
    return dates;
  }, [weekOffset]);

  // Filter sessions by selected date (single day) or visible range (full week/month)
  const filteredSessions = useMemo(() => {
    return sessions.filter((session: any) => {
      const sessionDate = session.scheduled_date?.split('T')[0];
      if (!sessionDate) return false;

      // Single day selected — filter to just that day
      if (selectedDate) {
        if (sessionDate !== selectedDate) return false;
      }
      // No day selected — show full visible range
      else if (visibleRange) {
        if (sessionDate < visibleRange.start || sessionDate > visibleRange.end) return false;
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          session.session_number?.toLowerCase().includes(searchLower) ||
          session.product_sku?.toLowerCase().includes(searchLower) ||
          session.product_name?.toLowerCase().includes(searchLower) ||
          session.raw_material_item_number?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [sessions, selectedDate, visibleRange, searchTerm]);

  // Group filtered sessions by date for display with date headers
  const sessionsByDate = useMemo(() => {
    if (selectedDate) return null; // Single day — no grouping needed
    const grouped: Record<string, any[]> = {};
    filteredSessions.forEach((s: any) => {
      const d = s.scheduled_date?.split('T')[0];
      if (d) {
        if (!grouped[d]) grouped[d] = [];
        grouped[d].push(s);
      }
    });
    // Sort dates ascending
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredSessions, selectedDate]);

  // Separate sessions into planned, active, completed, and cancelled
  const { plannedSessions, activeSessions, completedSessions, cancelledSessions } = useMemo(() => {
    const getEffectiveStatus = (s: any) => s.status;
    const planned = filteredSessions.filter((s: any) =>
      ['submitted', 'received', 'ready', 'unassigned', 'pending'].includes(getEffectiveStatus(s))
    );
    const active = filteredSessions.filter((s: any) =>
      ['active', 'in_progress', 'paused'].includes(getEffectiveStatus(s))
    );
    const completed = filteredSessions.filter((s: any) =>
      getEffectiveStatus(s) === 'completed'
    );
    const cancelled = filteredSessions.filter((s: any) =>
      getEffectiveStatus(s) === 'cancelled'
    );
    return { plannedSessions: planned, activeSessions: active, completedSessions: completed, cancelledSessions: cancelled };
  }, [filteredSessions]);

  // Get sessions count per date
  const sessionsPerDate = useMemo(() => {
    const counts: Record<string, number> = {};
    sessions.forEach((session: any) => {
      const date = session.scheduled_date?.split('T')[0];
      if (date) {
        counts[date] = (counts[date] || 0) + 1;
      }
    });
    return counts;
  }, [sessions]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return {
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      date: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
    };
  };


  // Handle cancel - close the add modal
  const handleCancel = () => {
    setIsAddingNew(false);
  };

  // Handle edit click — fetches full session data including materials
  const handleEditClick = async (session: any) => {
    setLoadingEditSession(true);
    try {
      const fullSession = await productionService.getProductionSession(session.session_number);
      setEditingSession(fullSession);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to load session for editing');
    } finally {
      setLoadingEditSession(false);
    }
  };

  // Handle cancel click
  const handleCancelClick = (sessionId: string) => {
    setCancelConfirmId(sessionId);
  };

  // Handle cancel-modal close
  const handleCancelClose = () => {
    setCancelConfirmId(null);
  };

  // Session being cancelled, resolved from the loaded list (the shared modal needs its name)
  const sessionToCancel = cancelConfirmId
    ? sessions.find((s: any) => s.session_number === cancelConfirmId) || { session_number: cancelConfirmId }
    : null;

  return (
    <div className="space-y-4">
      {/* Add / Edit Session Modal (reuses AddSessionModal for both) */}
      <AddSessionModal
        isOpen={isAddingNew || !!editingSession}
        onClose={() => {
          setIsAddingNew(false);
          setEditingSession(null);
        }}
        onSuccess={() => {
          queryClient.invalidateQueries(['productionSessions']);
          queryClient.invalidateQueries(['rawMaterials']);
        }}
        sessions={sessions}
        editSession={editingSession}
      />

      {/* Cancel session: shared reason dropdown + password confirmation (WCO-2706) */}
      <CancelSessionModal
        session={sessionToCancel}
        onClose={handleCancelClose}
      />

      {/* Calendar Week View - always visible */}
      <CalendarView
        sessions={sessions}
        onSessionClick={openSessionDetails}
        selectedDate={selectedDate}
        onDateSelect={(date) => {
          // Toggle: click same day = deselect (show full range), click different day = select it
          if (selectedDate === date) {
            setSelectedDate(null);
          } else {
            setSelectedDate(date);
          }
          // Calculate week offset for the selected date
          const today = new Date();
          const targetDate = new Date(date);
          const todayStartOfWeek = new Date(today);
          todayStartOfWeek.setDate(today.getDate() - today.getDay());
          todayStartOfWeek.setHours(0, 0, 0, 0);
          const targetStartOfWeek = new Date(targetDate);
          targetStartOfWeek.setDate(targetDate.getDate() - targetDate.getDay());
          targetStartOfWeek.setHours(0, 0, 0, 0);
          const diffTime = targetStartOfWeek.getTime() - todayStartOfWeek.getTime();
          const diffWeeks = Math.round(diffTime / (1000 * 60 * 60 * 24 * 7));
          setWeekOffset(diffWeeks);
        }}
        onRangeChange={(start, end) => {
          setVisibleRange({ start, end });
          // When navigating to a new range, clear single-day selection to show full range
          if (hasNavigated) {
            setSelectedDate(null);
          }
          setHasNavigated(true);
        }}
      />

      {/* Mode Toggle + Search and Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Calendar / Plan Mode Toggle */}
          {canManageSessions && (
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('view')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'view'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                <span className="hidden sm:inline">{t('planning.calendarView')}</span>
              </button>
              <button
                onClick={() => setViewMode('plan')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'plan'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                }`}
              >
                <ClipboardList className="w-4 h-4" />
                <span className="hidden sm:inline">{t('planning.planMode')}</span>
              </button>
            </div>
          )}
          {viewMode === 'view' && (
            <div className="relative flex-1 min-w-0 sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by SKU, product name, or session number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          )}
        </div>
        {viewMode === 'view' && (
          <div className="flex items-center gap-2">
            {/* Export Button */}
            <button
              onClick={() => setIsExportModalOpen(true)}
              className="flex items-center gap-2 px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline">{t('common.export')}</span>
            </button>
            {/* Add New Schedule Button - only for managers */}
            {canManageSessions && (
              <button
                onClick={() => setIsAddingNew(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">{t('supervisor.addNewSchedule')}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Plan Mode: WeeklyPlanningView */}
      {viewMode === 'plan' && (
        <WeeklyPlanningView weekDates={weekDates} selectedDate={selectedDate || new Date().toLocaleDateString('en-CA')} />
      )}

      {/* View Mode: Schedule Table */}
      {viewMode === 'view' && (<>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.sessionCode') || 'Session Code'}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.scheduleDate')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.scheduleItemNumber')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.scheduleProductName')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.packing')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.cases')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.estimatedLbs')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.material')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.scheduleBrand')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('supervisor.scheduleLocation')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('common.status')}
                </th>
                {canManageSessions && (
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={canManageSessions ? 12 : 11} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : filteredSessions.length === 0 ? (
                <tr>
                  <td colSpan={canManageSessions ? 12 : 11} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('supervisor.noScheduledSessions')}
                  </td>
                </tr>
              ) : sessionsByDate ? (
                /* Range mode: group sessions by date with date headers */
                <>
                  {sessionsByDate.map(([dateStr, dateSessions]) => {
                    const _d = new Date(dateStr + 'T00:00:00');
                    const dateLabel = `${_d.toLocaleDateString('en-US', { weekday: 'long' })} - ${String(_d.getMonth() + 1).padStart(2, '0')}/${String(_d.getDate()).padStart(2, '0')}/${_d.getFullYear()}`;
                    const isDateToday = dateStr === new Date().toLocaleDateString('en-CA');
                    return (
                      <React.Fragment key={dateStr}>
                        {/* Date header row */}
                        <tr>
                          <td
                            colSpan={canManageSessions ? 12 : 11}
                            className={`px-4 py-2 text-sm font-semibold border-b-2 ${
                              isDateToday
                                ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-600'
                                : 'bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                            }`}
                          >
                            {dateLabel}
                            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                              ({dateSessions.length} {dateSessions.length === 1 ? 'session' : 'sessions'})
                            </span>
                          </td>
                        </tr>
                        <SessionTableSection
                          sessions={dateSessions.filter((s: any) => ['active', 'in_progress', 'paused'].includes(s.status))}
                          label={t('supervisor.activeSessions')}
                          style={{ bannerBg: 'bg-green-50 dark:bg-green-900/20', bannerBorder: 'border-green-200 dark:border-green-800', bannerHover: 'hover:bg-green-100 dark:hover:bg-green-900/30', chevronColor: 'text-green-600 dark:text-green-400', dotColor: 'bg-green-500', dotExtra: 'animate-pulse', labelColor: 'text-green-700 dark:text-green-300' }}
                          isExpanded={true}
                          onToggleExpanded={() => {}}
                          canManageSessions={canManageSessions}
                          actionMode="cancel-only"
                          onSessionClick={openSessionDetails}
                          onCancelClick={handleCancelClick}
                        />
                        <SessionTableSection
                          sessions={dateSessions.filter((s: any) => ['submitted', 'received', 'ready', 'unassigned', 'pending'].includes(s.status))}
                          label={t('supervisor.plannedSessions')}
                          style={{ bannerBg: 'bg-blue-50 dark:bg-blue-900/20', bannerBorder: 'border-blue-200 dark:border-blue-800', bannerHover: 'hover:bg-blue-100 dark:hover:bg-blue-900/30', chevronColor: 'text-blue-600 dark:text-blue-400', dotColor: 'bg-blue-500', labelColor: 'text-blue-700 dark:text-blue-300' }}
                          isExpanded={true}
                          onToggleExpanded={() => {}}
                          canManageSessions={canManageSessions}
                          actionMode="edit-cancel"
                          onSessionClick={openSessionDetails}
                          onEditClick={handleEditClick}
                          onCancelClick={handleCancelClick}
                        />
                        <SessionTableSection
                          sessions={dateSessions.filter((s: any) => s.status === 'completed')}
                          label={t('supervisor.completedSessions')}
                          style={{ bannerBg: 'bg-gray-100 dark:bg-gray-700/50', bannerBorder: 'border-gray-200 dark:border-gray-600', bannerHover: 'hover:bg-gray-200 dark:hover:bg-gray-600/50', chevronColor: 'text-gray-600 dark:text-gray-400', dotColor: 'bg-gray-500', labelColor: 'text-gray-600 dark:text-gray-300' }}
                          isExpanded={true}
                          onToggleExpanded={() => {}}
                          rowOpacity="opacity-75"
                          canManageSessions={canManageSessions}
                          actionMode="none"
                          onSessionClick={openSessionDetails}
                        />
                        <SessionTableSection
                          sessions={dateSessions.filter((s: any) => s.status === 'cancelled')}
                          label={t('supervisor.cancelledSessions')}
                          style={{ bannerBg: 'bg-red-50 dark:bg-red-900/20', bannerBorder: 'border-red-200 dark:border-red-800', bannerHover: 'hover:bg-red-100 dark:hover:bg-red-900/30', chevronColor: 'text-red-600 dark:text-red-400', dotColor: 'bg-red-500', labelColor: 'text-red-600 dark:text-red-400' }}
                          isExpanded={false}
                          onToggleExpanded={() => {}}
                          rowOpacity="opacity-60"
                          canManageSessions={canManageSessions}
                          actionMode="none"
                          onSessionClick={openSessionDetails}
                        />
                      </React.Fragment>
                    );
                  })}
                </>
              ) : (
                /* Single day mode: existing status-grouped sections */
                <>
                  <SessionTableSection
                    sessions={activeSessions}
                    label={t('supervisor.activeSessions')}
                    style={{
                      bannerBg: 'bg-green-50 dark:bg-green-900/20',
                      bannerBorder: 'border-green-200 dark:border-green-800',
                      bannerHover: 'hover:bg-green-100 dark:hover:bg-green-900/30',
                      chevronColor: 'text-green-600 dark:text-green-400',
                      dotColor: 'bg-green-500',
                      dotExtra: 'animate-pulse',
                      labelColor: 'text-green-700 dark:text-green-300',
                    }}
                    isExpanded={isActiveExpanded}
                    onToggleExpanded={() => setIsActiveExpanded(!isActiveExpanded)}
                    canManageSessions={canManageSessions}
                    actionMode="cancel-only"
                    onSessionClick={openSessionDetails}
                    onCancelClick={handleCancelClick}
                  />

                  <SessionTableSection
                    sessions={plannedSessions}
                    label={t('supervisor.plannedSessions')}
                    style={{
                      bannerBg: 'bg-blue-50 dark:bg-blue-900/20',
                      bannerBorder: 'border-blue-200 dark:border-blue-800',
                      bannerHover: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
                      chevronColor: 'text-blue-600 dark:text-blue-400',
                      dotColor: 'bg-blue-500',
                      labelColor: 'text-blue-700 dark:text-blue-300',
                    }}
                    isExpanded={isPlannedExpanded}
                    onToggleExpanded={() => setIsPlannedExpanded(!isPlannedExpanded)}
                    canManageSessions={canManageSessions}
                    actionMode="edit-cancel"
                    onSessionClick={openSessionDetails}
                    onEditClick={handleEditClick}
                    onCancelClick={handleCancelClick}
                  />

                  <SessionTableSection
                    sessions={completedSessions}
                    label={t('supervisor.completedSessions')}
                    style={{
                      bannerBg: 'bg-gray-100 dark:bg-gray-700/50',
                      bannerBorder: 'border-gray-200 dark:border-gray-600',
                      bannerHover: 'hover:bg-gray-200 dark:hover:bg-gray-600/50',
                      chevronColor: 'text-gray-600 dark:text-gray-400',
                      dotColor: 'bg-gray-500',
                      labelColor: 'text-gray-600 dark:text-gray-300',
                    }}
                    isExpanded={isCompletedExpanded}
                    onToggleExpanded={() => setIsCompletedExpanded(!isCompletedExpanded)}
                    rowOpacity="opacity-75"
                    canManageSessions={canManageSessions}
                    actionMode="none"
                    onSessionClick={openSessionDetails}
                  />

                  <SessionTableSection
                    sessions={cancelledSessions}
                    label={t('supervisor.cancelledSessions')}
                    style={{
                      bannerBg: 'bg-red-50 dark:bg-red-900/20',
                      bannerBorder: 'border-red-200 dark:border-red-800',
                      bannerHover: 'hover:bg-red-100 dark:hover:bg-red-900/30',
                      chevronColor: 'text-red-600 dark:text-red-400',
                      dotColor: 'bg-red-500',
                      labelColor: 'text-red-600 dark:text-red-400',
                    }}
                    isExpanded={isCancelledExpanded}
                    onToggleExpanded={() => setIsCancelledExpanded(!isCancelledExpanded)}
                    rowOpacity="opacity-60"
                    canManageSessions={canManageSessions}
                    actionMode="none"
                    onSessionClick={openSessionDetails}
                  />
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Footer */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 md:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            {filteredSessions.length} sessions
            {selectedDate
              ? (() => { const _d = new Date(selectedDate + 'T00:00:00'); return ` for ${_d.toLocaleDateString('en-US', { weekday: 'long' })} - ${String(_d.getMonth() + 1).padStart(2, '0')}/${String(_d.getDate()).padStart(2, '0')}/${_d.getFullYear()}`; })()
              : visibleRange
              ? ` in range`
              : ''}
          </span>
          <div className="flex items-center gap-4">
            <span className="text-gray-600 dark:text-gray-400">
              Total Cases:{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {filteredSessions.reduce(
                  (sum: number, s: any) => sum + (Number(s.total_boxes) || 0),
                  0
                )}
              </span>
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              Total Est. lbs:{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {filteredSessions
                  .reduce(
                    (sum: number, s: any) =>
                      sum + (Number(s.target_output_weight) || 0),
                    0
                  )
                  .toFixed(2)}
              </span>
            </span>
          </div>
        </div>
      </div>
      </>)}

      {/* Session Details Modal */}
      {selectedSession && (
        <SessionDetailsModal
          session={selectedSession}
          onClose={closeSessionDetails}
          initialTab={getDefaultSessionTab(user?.role)}
        />
      )}

      {/* Export Sessions Modal */}
      <ExportSessionsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        sessions={sessions}
        defaultStartDate={weekDates[0]}
        defaultEndDate={weekDates[6]}
      />
    </div>
  );
};

export default ManagerScheduleView;
